import _ from 'lodash'
// import _ from 'lodash'
import {select as d3Select} from 'd3-selection'
import 'd3-selection-multi'
import {scaleLinear} from 'd3-scale'
import {interpolateRgb} from 'd3-interpolate'
const EASE_OUT_EXPO = "0.190 1 0.220 1" // use http://matthewlein.com/ceaser/ for spline handle generators
// const EASE_IN_OUT_QUINT = "0.860, 0.000, 0.070, 1.000"
const EASE_IN_OUT_QUAD = "0.455, 0.030, 0.515, 0.955"

const DEFAULT_GLOBAL_ID = "global_defs"

const gradAttrs = {
  horizontal: {
    x1: '0%',
    y1: '0%',
    x2: '100%',
    y2: '0%'
  },
  vertical: {
    x1: '0%',
    y1: '0%',
    x2: '0%',
    y2: '100%'
  },
  radial: {
    gradientUnits: "userSpaceOnUse",
    cx: 0,
    cy: 0,
    fx: 0,
    fy: 0,
    r: '50%'
  }
}

const appendGradient = (gradType, id, colorStops, globalId, gradAttr) => {
  globalId = globalId || DEFAULT_GLOBAL_ID
  // TODO make this accept an arg hash, to allow optional params
  if (!d3Select(`#${ id }`).empty()) { return false }
  if (gradAttr == null) {
    gradAttr = gradAttrs[gradType] || _.first(gradAttrs)
  }
  const defs = d3Select(`#${ globalId }`)
  const gradient = defs.append('svg:linearGradient')
    .attr('id', id)
    .attr('gradientUnits', 'userSpaceOnUse')
    .attrs(gradAttr)
  colorStops.forEach((colorStop) => {
    gradient.append("svg:stop")
      .attrs({
        offset: colorStop.stop,
        "stop-color": colorStop.color,
        "stop-opacity": _.isUndefined(colorStop.opacity) ? 1 : colorStop.opacity
      })
  })
  return gradient
}

const appendRadialGradient = (id, colorStops, globalId, gradAttr) => {
  globalId = globalId || DEFAULT_GLOBAL_ID
  if (!d3Select(`#${ id }`).empty()) { return false }
  if (gradAttr == null) {
    gradAttr = gradAttrs.radial
  }
  const defs = d3Select(`#${ globalId }`)
  const gradient = defs.append('svg:radialGradient')
    .attr('id', id)
    .attr('gradientUnits', 'userSpaceOnUse')
    .attrs(gradAttr)
  colorStops.forEach((colorStop) => {
    gradient.append("svg:stop")
      .attrs({
        offset: colorStop.stop,
        "stop-color": colorStop.color,
        "stop-opacity": _.isUndefined(colorStop.opacity) ? 1 : colorStop.opacity
      })
  })
  return gradient
}

const appendAnimatedGradient = (gradType, id, colorStops, globalId, gradAttr) => {
  globalId = globalId || DEFAULT_GLOBAL_ID
  if (!d3Select(`#${ id }`).empty()) { return false }
  if (gradAttr == null) {
    gradAttr = gradAttrs[gradType] || _.first(gradAttrs)
  }
  const defs = d3Select(`#${ globalId }`)
  const gradient = defs.append('svg:linearGradient')
    .attr('id', id)
    .attrs(gradAttr)
  colorStops.forEach((colorStop) => {
    const stop = gradient.append("svg:stop")
      .attrs({
        offset: colorStop.stop,
        "stop-color": colorStop.color,
        "stop-opacity": _.isUndefined(colorStop.opacity) ? 1 : colorStop.opacity
      })
    if (colorStop.animateValues) {
      //append animate only to last
      stop.append("svg:animate").attrs({
        attributeName: 'offset',
        begin: '0s',
        dur: '2.5s',
        repeatCount: "indefinite",
        values: colorStop.animateValues,
        calcMode: "spline",
        keySplines: EASE_IN_OUT_QUAD,
        keyTimes: "0;1"
      })
    }
  })
  return gradient
}



const appendRotatingGradient = (id, colorStops, globalId, gradAttr, animationAttr) => {
  globalId = globalId || DEFAULT_GLOBAL_ID
  if (!d3Select(`#${ id }`).empty()) { return false }
  if (gradAttr == null) {
    gradAttr = _.first(gradAttrs)
  }
  const defs = d3Select(`#${ globalId }`)
  const gradient = defs.append('svg:linearGradient')
    .attrs({
      'id': id,
      'gradientTransform': 'rotate(0)'
    })
    .attrs(gradAttr)
  gradient.append("svg:animateTransform").attrs({
    attributeName: "gradientTransform",
    attributeType: "XML",
    type: "rotate",
    repeatCount: "indefinite"
    // from: 270
    // to: 405
    // dur: '4s'
    // begin: "8s"
    // calcMode: "spline"
    // keySplines: EASE_IN_OUT_QUAD
    // keyTimes:"0;1"
  })
  .attrs(animationAttr)
  colorStops.forEach((colorStop) => {
    gradient.append("svg:stop")
      .attrs({
        offset: colorStop.stop,
        "stop-color": colorStop.color,
        "stop-opacity": _.isUndefined(colorStop.opacity) ? 1 : colorStop.opacity
      })
  })
  return gradient
}



const appendBlurFilter = (filterId, appendTarget, blurValue, animateMode) => {
  //animateMode (optional) should be "in" or "out". In == start blurred, animating to sharp.
  const filter = appendTarget.append('filter')
    .attr('id', filterId)
    .append('svg:feGaussianBlur')
    .attr('stdDeviation', animateMode && animateMode === "in" ? 0 : blurValue) // after animation ends, blur will return to ths value)
  if (animateMode) {
    filter.append("svg:animate").attrs({
      attributeName: 'stdDeviation',
      begin: '0s',
      dur: '2s',
      repeatCount: "indefinite",
      values: animateMode === "in" ? "#{blurValue};0" : "0;#{blurValue}",
      calcMode: "spline",
      keySplines: EASE_OUT_EXPO,
      keyTimes: "0;1"
    })
  }
  return filter
}

export default {
  appendBlurFilter: appendBlurFilter,
  appendGradient: appendGradient,
  appendRadialGradient: appendRadialGradient,
  appendAnimatedGradient: appendAnimatedGradient,
  appendRotatingGradient: appendRotatingGradient,
  appendHorizontalGradient: _.partial(appendGradient, "horizontal"),
  appendVerticalGradient: _.partial(appendGradient, "vertical"),
  createGradientColorScales: function(colorStops, maxValue) {
    var highStop, i, j, lowStop, ref, results
    results = []
    for (i = j = 0, ref = colorStops.length - 1; j < ref; i = j += 1) {
      lowStop = colorStops[i]
      highStop = colorStops[i + 1]
      results.push(scaleLinear().domain([lowStop.stop * maxValue, highStop.stop * maxValue]).interpolate(interpolateRgb).range([lowStop.color, highStop.color]))
    }
    return results
  },
  getGradColor: function(colorScales, val) {
    var colorScale, i, j, len
    for (i = j = 0, len = colorScales.length; j < len; i = ++j) {
      colorScale = colorScales[i]
      if (val >= colorScale.domain()[0] && val <= colorScale.domain()[1]) {
        return colorScale(val)
      }
    }
  }
  // createGradientColorScales: (colorStops, maxValue) => {
    // for i in [0...colorStops.length - 1] by 1
      // lowStop = colorStops[i]
      // highStop = colorStops[i + 1]
      // scaleLinear)
      // .domain([lowStop.stop * maxValue, highStop.stop * maxValue])
      // .interpolate(interpolateRgb)
      // .range([lowStop.color, highStop.color])
    // return _.map(colorStops, (lowStop, i) => {
    //   if (i === colorStops.length) {
    //     return false
    //   }
    //   let highStop = colorStops[i + 1]
    //   return scaleLinear)
    //     .domain([lowStop.stop * maxValue, highStop.stop * maxValue])
    //     .interpolate(interpolateRgb)
    //     .range([lowStop.color, highStop.color])
    // })
  //   var highStop, i, j, lowStop, ref
  //   for (i = j = 0, ref = colorStops.length - 1; j < ref; i = j += 1) {
  //     lowStop = colorStops[i]
  //     highStop = colorStops[i + 1]
  //     scaleLinear).domain([lowStop.stop * maxValue, highStop.stop * maxValue]).interpolate(interpolateRgb).range([lowStop.color, highStop.color])
  //   }
  // },
  // getGradColor: (colorScales, val) => {
  //   _.forEach(colorScales, (colorScale) => {
  //     if (val >= colorScale.domain()[0] && val <= colorScale.domain()[1]) {
  //       return colorScale(val)
  //     }
  //   })
  // }
}
